import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ServiceComparisonMatrix = ({ pricingTiers }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('NGN');

  const currencyOptions = [
    { value: 'NGN', label: '₦ Nigerian Naira (NGN)' },
    { value: 'XAF', label: 'XAF Central African CFA' },
    { value: 'ZAR', label: 'R South African Rand (ZAR)' }
  ];

  const formatPrice = (prices) => {
    const currencySymbols = {
      NGN: '₦',
      XAF: 'XAF',
      ZAR: 'R'
    };
    return `${currencySymbols?.[selectedCurrency]}${prices?.[selectedCurrency]?.toLocaleString()}`;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
      <div className="text-center mb-6 md:mb-8 lg:mb-12">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-3 md:mb-4"
        >
          Service Packages & Pricing
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-sm md:text-base lg:text-lg text-muted-foreground mb-6 md:mb-8"
        >
          Choose the perfect package that fits your needs and budget
        </motion.p>

        <div className="max-w-xs mx-auto">
          <Select
            options={currencyOptions}
            value={selectedCurrency}
            onChange={setSelectedCurrency}
            placeholder="Select currency"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
        {pricingTiers?.map((tier, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className={`relative rounded-xl p-6 md:p-8 border-2 transition-all duration-300 ${
              tier?.popular
                ? 'border-primary bg-primary/5 shadow-xl scale-105'
                : 'border-border bg-muted hover:border-primary/50'
            }`}
          >
            {tier?.popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 bg-primary text-primary-foreground text-xs md:text-sm font-medium rounded-full">
                  Most Popular
                </span>
              </div>
            )}

            <div className="text-center mb-6 md:mb-8">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon name={tier?.icon} size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">{tier?.name}</h3>
              <p className="text-xs md:text-sm text-muted-foreground mb-4">{tier?.description}</p>
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  {formatPrice(tier?.price)}
                </span>
                <span className="text-sm md:text-base text-muted-foreground">/{tier?.billingPeriod}</span>
              </div>
            </div>

            <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
              {tier?.features?.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.05 * idx }}
                  className="flex items-start gap-3"
                >
                  <Icon
                    name={feature?.included ? 'CheckCircle2' : 'XCircle'}
                    size={18}
                    color={feature?.included ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    className="flex-shrink-0 mt-0.5"
                  />
                  <span
                    className={`text-xs md:text-sm ${
                      feature?.included ? 'text-foreground' : 'text-muted-foreground line-through'
                    }`}
                  >
                    {feature?.text}
                  </span>
                </motion.div>
              ))}
            </div>

            <Button
              variant={tier?.popular ? 'default' : 'outline'}
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
            >
              {tier?.ctaText}
            </Button>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-8 md:mt-12 text-center"
      >
        <p className="text-xs md:text-sm text-muted-foreground mb-4">
          Need a custom solution? Let's discuss your specific requirements
        </p>
        <Button variant="secondary" iconName="MessageCircle" iconPosition="left">
          Request Custom Quote
        </Button>
      </motion.div>
    </div>
  );
};

export default ServiceComparisonMatrix;